import React from 'react';
import { useAPI } from 'lib/API';
import { createInviteV2 } from 'requests/invite';
import { updateUserProfile } from 'requests/userProfile';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { GENERAL_CONSTANTS } from 'constants/general';
import { Role } from 'data/User';

interface UserInviteForm {
    name: string;
    email: string;
    vendor: string;
    businessUnit: string;
    userRole: { id: string; value: string };
    ginId?: string;
}

interface UseUserInvitationProps {
    onSuccess?: () => void;
    businessUnitId?: string;
    vendorId?: string;
    viewMode?: string
}

export const useUserInvitation = ({ onSuccess, businessUnitId, vendorId, viewMode }: UseUserInvitationProps = {}) => {
    const [showInviteModal, setShowInviteModal] = React.useState(false);
    const [userProfileId, setUserProfileId] = React.useState<number>();
    const [alreadyInvitedRole, setAlreadyInvitedRole] = React.useState('');
    const [inviteErrors, setInviteErrors] = React.useState<Partial<Record<keyof UserInviteForm, string>>>({});

    // Initialize invite form
    const [inviteForm, setInviteForm] = React.useState<UserInviteForm>({
        name: '',
        email: '',
        vendor: vendorId || '',
        businessUnit: businessUnitId || '',
        userRole: { id: '', value: '' },
        ginId: ''
    });

    // Reset form to initial state
    const handleFormReset = () => {
        setInviteErrors({});
        setInviteForm({
            name: '',
            email: '',
            vendor: vendorId || '',
            businessUnit: businessUnitId || '',
            userRole: { id: '', value: '' },
            ginId: ''
        });
        setAlreadyInvitedRole('');
    };

    // Toggle invite modal
    const toggleInviteModal = () => {
        if (showInviteModal) {
            handleFormReset();
        }
        setShowInviteModal(!showInviteModal);
        setUserProfileId(undefined);
    };

    // Handle input changes
    const handleInviteInputChange = (field: keyof UserInviteForm | string, value: any) => {
        setInviteForm(prev => ({ ...prev, [field]: value }));
        setInviteErrors(prev => ({ ...prev, [field]: '' }));
    };

    // Validate form
    const validateInviteForm = () => {
        const errors: any = {};

        // Check if selected role is Admin or SuperAdmin
        const selectedRoleIsAdmin = inviteForm.userRole?.id === Role.Admin ||
                                     inviteForm.userRole?.id === Role.SuperAdmin;
        const selectedRoleIsVendorAdmin = inviteForm.userRole?.id === Role.VendorAdmin;

        if (!inviteForm.name.trim()) errors.name = GENERAL_CONSTANTS.NAME_REQUIRED;
        if (!inviteForm.email.trim()) errors.email = GENERAL_CONSTANTS.EMAIL_REQUIRED;
        else if (!/\S+@\S+\.\S+/.test(inviteForm.email)) errors.email = GENERAL_CONSTANTS.INVALID_EMAIL;
        if (!inviteForm.userRole?.id) errors.userRole = GENERAL_CONSTANTS.USER_ROLE_REQUIRED;

        // Business Unit is not required for Admin or VendorAdmin roles
        if (!selectedRoleIsAdmin && !selectedRoleIsVendorAdmin) {
            if (!inviteForm.businessUnit) {
                errors.businessUnit = GENERAL_CONSTANTS.BUSINESS_UNIT_REQUIRED;
            }
        }

        if (Object.keys(errors).length > 0) {
            setInviteErrors(errors);
            return true;
        }

        return false;
    };

    // API requests for user invitation
    const inviteUserReq = useAPI({
        deferFn: createInviteV2,
        onResolve: result => {
            renderToast(toast.TYPE.SUCCESS, GENERAL_CONSTANTS.INVITE_SENT_SUCCESS);
            toggleInviteModal();
            if (onSuccess) onSuccess();
        },
         onReject: (err: any) => {
            if (err.data?.role) {
                setAlreadyInvitedRole(err.data?.role);
            } else if(err.message){
                setAlreadyInvitedRole(err.message);
            }else {
                renderToast(toast.TYPE.ERROR, err.message);
            }
        }
    });

    // API requests for user profile update
    const updateUserProfileReq = useAPI({
        deferFn: updateUserProfile,
        onResolve: result => {
            renderToast(toast.TYPE.SUCCESS, GENERAL_CONSTANTS.USER_PROFILE_UPDATED);
            toggleInviteModal();
            setUserProfileId(undefined);
            if (onSuccess) onSuccess();
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    // Send invitation or update user profile
    const handleInviteSend = async () => {
        const hasErrors = validateInviteForm();

        if (!hasErrors) {
            if (userProfileId) {
                const payload = {
                    name: inviteForm.name,
                    email: inviteForm.email,
                    businessUnit: (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) ? inviteForm.businessUnit : businessUnitId,
                    role: inviteForm.userRole?.id,
                    ginId: inviteForm.ginId,
                    vendorId: inviteForm.vendor
                };
                updateUserProfileReq.run(userProfileId, payload);
            } else {
                inviteUserReq.run(
                    inviteForm.email, 
                    inviteForm.name, 
                    inviteForm.userRole.id, 
                    (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) ? inviteForm.businessUnit : businessUnitId,
                    inviteForm.ginId,
                    inviteForm.vendor
                );
            }
        }
    };

    return {
        // State
        showInviteModal,
        userProfileId,
        inviteForm,
        inviteErrors,
        alreadyInvitedRole,
        isLoading: updateUserProfileReq.isLoading || inviteUserReq.isLoading,
        
        // Actions
        toggleInviteModal,
        handleInviteSend,
        handleInviteInputChange,
        setUserProfileId,
        setAlreadyInvitedRole,
        handleFormReset
    };
};
